<template>
    <video-player
        ref="videoPlayer"
        :options="compiledVjsOptions"
        @ready="$emit('ready', $event)">
    </video-player>
</template>

<script>
    import MpegDashUtils   from '../../../packages/mpeg-dash-utils';
    import S3Utils         from '../../../packages/s3-utils';
    import Aws             from '../../../services/aws';
    import { videoPlayer } from 'vue-video-player';

    require('./quality-selector');
    require('./quality-selector.css');

    async function getMp4VideoJsSource(s3Url) {
        const s3Obj = S3Utils.parseS3Url(s3Url);
        const signedUrl = (
            await Aws.getPresignedUrl(
                null,
                s3Obj.bucket,
                s3Obj.key,
                'getObject',
            )
        ).data;
        return {
            src: signedUrl,
            type: 'video/mp4',
        };
    }

    function setDashQuality(player, quality) {
        const dash = (player.dash || {}).mediaPlayer;
        if (quality === 'Auto') {
            dash.setAutoSwitchQualityFor('video', true);
            dash.setLimitBitrateByPortal(true);
        } else {
            dash.setAutoSwitchQualityFor('video', false);
            dash.setLimitBitrateByPortal(false);
            dash.setQualityFor('video', {
                '480p':  0,
                '720p':  1,
                '1080p': 2,
            }[quality]);
        }
    }

    function flushPlayerBuffers(player) {
        // There is no public API for flushing the buffered video data, so we'll
        // simply change the time, wait for the buffers to empty, and then change
        // the time back.
        // player.on('loadeddata', ...) would have been better than setTimeout(),
        // but it doesn't seem to fire in this case, so the timeout is sort of
        // best effort.
        const time = player.currentTime();
        player.currentTime(0);
        setTimeout(() => player.currentTime(time), 500);
    }

    function hasDashSupport() {
        // iOS & Safari detection taken from:
        //   https://stackoverflow.com/a/29696509
        const ua = window.navigator.userAgent;
        const iOS = !!ua.match(/iP(ad|od|hone)/i);
        const webkit = !!ua.match(/WebKit/i);
        const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
        return !iOSSafari;
    }

    // TODO: remove this once we implement HLS
    // This was added as a quick patch since Safari on iOS devices don't support
    // dash, and some of the original videos are in webm (which Safari also
    // doesn't support).
    function avoidWebmSource(s3Src) {
        if (/\.webm$/.test(s3Src)) {
            const s3Parts = S3Utils.parseS3Url(s3Src);

            /* According to Matt, there are no webm training videos, so we can
             * get rid of this branch.
            if (s3Parts.bucket === 'ttto-course-videos-raw') {
                const baseKey = s3Parts.key.match(/^(.+)\.webm$/)[1];
                const newKey = `${baseKey}/${baseKey}-1080.mp4`;
                return S3Utils.buildS3Url('ttto-course-videos', newKey);
            }
            */
            if (s3Parts.bucket === 'ttto-feedback') {
                const baseKey = s3Parts.key.match(/^(.+)\.webm$/)[1];
                const newKey = `${baseKey}.mp4`;
                return S3Utils.buildS3Url('ttto-feedback-transcoded', newKey);
            }

            // Fallback to the webm source, we can't avoid it
            return s3Src;

        } else /* not webm */ {
            return s3Src;
        }
    }

    function doesSupportQualitySelector(vjsSrc) {
        return MpegDashUtils.isVideoJsSourceDash(vjsSrc);
    }

    function compileVjsOptions(defaultOptions, extraOptions) {
        return Object.assign(
            {},
            defaultOptions,
            extraOptions,
            {
                plugins: Object.assign(
                    {},
                    (defaultOptions || {}).plugins,
                    (extraOptions || {}).plugins,
                ),
            },
        );
    }

    /**
     * @returns {'1080p'|'720p'|'480p'|'Auto'}
     */
    function loadQualityFromStore() {
        let preferredRes;
        try {
            preferredRes = localStorage.getItem('preferred_resolution');
        } catch (err) {
            // Ignore errors; we'll just return auto
        }
        return {
            '1080': '1080p',
            '720': '720p',
            '480': '480p',
        }[preferredRes] || 'Auto';
    }

    /**
     * @param {'1080p'|'720p'|'480p'|'Auto'} quality
     */
    function saveQualityToStore(quality) {
        const preferredRes = {
            '1080p': '1080',
            '720p': '720',
            '480p': '480',
        }[quality.toLowerCase()] || 'auto';
        try {
            localStorage.setItem('preferred_resolution', preferredRes);
        } catch (err) {
            // Ignore errors
        }
    }

    /**
     * @returns {'3x'|'2x'|'1.5x'|'1x'|'0.7x'}
     */
    function loadSpeedFromStore() {
        let preferredSpeed;
        try {
            preferredSpeed = localStorage.getItem('preferred_speed');
        } catch (err) {
            // Ignore errors; we'll just return 1x
        }
        return preferredSpeed || '1x';
    }

    /**
     * @param {'3x'|'2x'|'1.5x'|'1x'|'0.7x'} speed
     */
    function saveSpeedToStore(speed) {
        const preferredSpeed = speed || '1x';
        try {
            localStorage.setItem('preferred_speed', preferredSpeed);
        } catch (err) {
            // Ignore errors
        }
    }

    export default {
        name: 'poly-video',

        /**
         * Events emitted:
         *  - ended
         *  - error        - when sources are provided but none can be used
         *  - loadeddata
         *  - loadedsource - when new source is loaded (scrubbing not included)
         *  - pause
         *  - play
         *  - ready
         *  - timeupdate
         */

        components: {
            videoPlayer,
        },

        props: {
            sources: {
                type: Array,
                // List of URLs - supports S3
                // The list should be ranked in priority. The first source that
                // can be applied to the Video.js player will be used.
            },
            vjsOptions: {
                type: Object,
                // Video.js options to pass through
                // Currently, this should be a static object. Changing the
                // options after poly-video has been mounted results in
                // undefined behavior.
                // This is due to vue-video-player not emitting a "ready" event
                // after re-initilizing the video player.
            },
            dashEnabled: {
                type: Boolean,
                default: true,
                // Use MPEG DASH streaming if available
            },
            playOnLoad: {
                type: Boolean,
                default: false,
                // This will call play() on the player once the video has loaded.
                // This is a more aggressive form of the `autoplay` attribute,
                // since browsers normally reject that request.
            },
            persistPlaybackSpeed: {
                type: Boolean,
                default: false,
                // This will load the last-used playback speed from local
                // storage, as well as save any speed changes.
            },
            persistPlaybackQuality: {
                type: Boolean,
                default: false,
                // This will load the last-used playback quality from local
                // storage, as well as save any quality changes.
            },
        },

        data() {
            return {
                defaultVjsOptions: {
                    autoplay: false,
                    controls: true,
                    preload: 'auto',
                    sources: [],
                    html5: {
                        dash: {
                            // Start video out at max quality, then degrade.
                            setInitialBitrateFor: ['video', 10000],

                            // Limit the playback quality in smaller players.
                            // This saves bandwidth and avoids sharp pixelation
                            // artifacts that come from downsizing higher quality
                            // stream. Also, assuming fast switching is enabled,
                            // switching from a small player to fullscreen will
                            // upgrade the quality within 3 - 9 seconds (assuming
                            // a sufficient download speed).
                            setLimitBitrateByPortal: true,

                            // Use higher quality stream as soon available,
                            // rather than waiting to burn through the all of the
                            // lower quality content that has already been
                            // buffered.
                            setFastSwitchEnabled: true,

                            // Allow a 1 second gap between when the video stream
                            // and audio stream start.
                            // Wrike issue:
                            //   https://www.wrike.com/open.htm?id=342403920
                            // Dash.js bug report:
                            //   https://github.com/Dash-Industry-Forum/dash.js/issues/2513
                            // Watch this for when a fix is built into Dash.js:
                            //   https://github.com/Dash-Industry-Forum/dash.js/issues/2565
                            setSegmentOverlapToleranceTime: 1.0,
                        },
                    },
                    plugins: {},
                },
                qualitySelectorOptions: {
                    defaultQuality: 'Auto',
                },
                uippInterval: null,
            };
        },

        mounted() {
            this.$on('ready', this.initPlayer);
        },

        beforeDestroy() {
            clearInterval(this.uippInterval);
            this.uippInterval = null;
        },

        methods: {
            getVjsPlayer() {
                return (this.$refs.videoPlayer || {}).player;
            },

            initPlayer() {
                // Pass thru events
                const player = this.getVjsPlayer();
                [
                    'ended',
                    'loadeddata',
                    'pause',
                    'play',
                    'timeupdate',
                ].forEach(ev => {
                    player.on(ev, () => this.$emit(ev, player));
                });

                // The quality selector plugin only handles the UI side of
                // things; we have to implement the functionality.
                player.on('qualitychange', () => {

                    // Update the dash player settings
                    const quality = player.currentQuality();
                    setDashQuality(player, quality);

                    // Flush the buffers to force the quality change
                    // DISABLED: this seems to corrupt video playback on laggy
                    // networks. To reproduce:
                    //  1) Enable the code below
                    //  2) Within Chrome, enable a 250ms latency delay (if
                    //     troubleshooting, try higher values)
                    //  3) Go to the Portal video player and switch your quality
                    //     preference to 1080p
                    //  4) If you don't experience the video artifacts after
                    //     about 20 seconds, try refreshing the browser
                    if (quality !== 'Auto') {
                        //flushPlayerBuffers(player);
                    }

                    // Persist the new quality setting
                    if (this.persistPlaybackQuality) {
                        saveQualityToStore(quality);
                    }
                });

                // Persist the playback rate
                player.on('ratechange', () => {
                    if (this.persistPlaybackSpeed) {
                        const rate = player.playbackRate();
                        const rateStr = `${rate}x`;
                        saveSpeedToStore(rateStr);
                    }
                });

                // Load the sources
                this.resolveSources();

                // Set up the user id pixel pattern overlay
                this.initUipp();
            },

            initUipp() {
                // Configures the User Id Pixel Pattern (UIPP)

                // Create the overlay container. There are a few things we do in
                // order to help disguise the overlay:
                //
                //  1) We use a wrapper so that the element doesn't flash in the
                //     console when adding/removing the "on" class in our
                //     interval.
                //  2) Inserting the element in the middle of the rest of the
                //     video-js components, rather than simply appending it.
                //  3) Adding a bunch of non-sense in the form of vjs classes
                //     and attributes. This helps it blend in better.
                //
                const vEl = $(this.$el).find('.video-js');
                vEl.children('.uipp-wrapper').remove();
                const wEl = $(`
                    <div class="uipp-wrapper">
                        <div class="uipp"></div>
                    </div>
                `).insertAfter(vEl.children('.vjs-tech').last());
                const oEl = wEl.children('.uipp');

                // Add camo in the form of video-js non-sense
                wEl.removeClass('uipp-wrapper');
                wEl.addClass('vjs-tech-assist uipp-wrapper vjs-plg');
                wEl.attr('tabindex', -1);
                wEl.attr('aria-hidden', true);

                // We'll be rendering the user's id as pixels, so we need to
                // convert the id to bits.
                const userIdBits = (() => {
                    const userId = parseInt(localStorage.user_id);
                    const bits = [];
                    for (let i = 0; i < 32; ++i) {
                        const bit = (userId & (1 << i)) > 0;
                        bits.push(bit);
                    }
                    return bits;
                })();

                // The pattern consists of X number of bits representing the
                // user's id, sandwiched between two start/end pixels. The
                // start and end pixels are reference points, so we can
                // determine the distance between bits in case the video is
                // cropped.
                oEl.append('<div class="start" />');
                for (const b of userIdBits.reverse()) {
                    if (b > 0) {
                        oEl.append('<div class="on" />');
                    } else {
                        oEl.append('<div />');
                    }
                }
                oEl.append('<div class="end" />');

                // The pixels are off by default, so we need to flash them
                // occasionally. This flash is intended to be unnoticable
                // during regular viewing, but will show up on certain
                // frames in any screen recordings.
                //
                // I tried using a CSS animation for this instead, but for
                // whatever reason, it was _very_ noticable.
                clearInterval(this.uippInterval);
                this.uippInterval = setInterval(() => {
                    const duration = 1 / 30 * 1000; // 1 / 30 FPS
                    oEl.addClass('on');
                    setTimeout(() => oEl.removeClass('on'), duration);
                }, 5000);
            },

            async resolveSources() {

                // If the player hasn't been loaded yet, we can't resolve sources
                if (!this.getVjsPlayer()) {
                    return;
                }

                // Convert the input sources to vjs sources
                let vjsSources = [];
                for (const src of this.sources) {

                    // We can only use S3 URLs
                    if (!S3Utils.isS3Url(src)) {
                        continue;
                    }

                    // Convert to vjs source object
                    let vjsSrc;
                    try {
                        if (this.dashEnabled && hasDashSupport()) {
                            vjsSrc = await MpegDashUtils.getVideoJsSource(src);
                        } else {
                            const saferSrc = avoidWebmSource(src);
                            vjsSrc = await getMp4VideoJsSource(saferSrc);
                        }
                    } catch (err) {
                        console.log('failed to get vjs source:', err);
                        continue;
                    }

                    // Save the source
                    vjsSources.push(vjsSrc);
                }

                // If we have a valid source we can trigger the autoplay
                if (this.playOnLoad && vjsSources.length > 0) {
                    this.$once('loadedsource', () => {
                        this.getVjsPlayer().play();
                    });
                }

                // Load the persists playback settings once the video has loaded
                if (vjsSources.length > 0) {
                    this.$once('loadedsource', () => {
                        const player = this.getVjsPlayer();

                        // Load quality
                        if (this.persistPlaybackQuality && player.currentQuality) {
                            const lastQuality = loadQualityFromStore();
                            player.currentQuality(lastQuality);
                        }

                        // Load speed
                        if (this.persistPlaybackSpeed) {
                            const lastRateStr = loadSpeedFromStore();
                            const lastRate = parseFloat(lastRateStr.match(/^(.+)x$/)[1]);
                            player.playbackRate(lastRate);
                        }
                    });
                }

                // If we have a valid source we can fire the `loadedsource` event
                if (vjsSources.length > 0) {
                    this.$once('loadeddata', () => {
                        this.$emit('loadedsource', this.getVjsPlayer());
                    });
                }

                // Save the converted sources
                if (vjsSources.length > 0) {
                    this.getVjsPlayer().src(vjsSources);
                }

                // Show/hide the quality selector
                if (doesSupportQualitySelector(vjsSources[0] || {})) {
                    const player = this.getVjsPlayer();
                    if (!player.qualitySelectorPlugin_) {
                        player.qualitySelector(this.qualitySelectorOptions);
                    }
                } else {
                    // TODO: remove plugin if exists
                }

                // If sources were provided, but none of them could be used, emit
                // an error
                const hasInputSources = this.sources && this.sources.length > 0;
                const hasPlayerSources = vjsSources.length > 0;
                if (hasInputSources && !hasPlayerSources) {
                    this.$emit('error');
                }
            },
        },

        computed: {
            compiledVjsOptions() {
                return compileVjsOptions(
                    this.defaultVjsOptions,
                    this.vjsOptions,
                );
            },
        },

        watch: {
            sources: {
                handler: function(newSources, oldSources) {
                    const newSourcesStr = JSON.stringify(newSources);
                    const oldSourcesStr = JSON.stringify(oldSources);
                    if (newSourcesStr !== oldSourcesStr) {
                        this.resolveSources();
                    }
                },
            },
        },
    }
</script>

<style lang="scss" scoped>
</style>

<style lang="scss">
    .video-js .uipp {
        position: absolute;
        top: 50%;
        left: 15%;
        right: 15%;
        display: flex;
        justify-content: space-between;

        div {
            width: 2px;
            height: 2px;

            &.start {
                background: black;
                transform: translate(-50%, -250%);
            }
            &.on {
                background: black;
                transform: translate(-50%, -50%);
            }
            &.end {
                background: black;
                transform: translate(-50%, 150%);
            }
        }

        &:not(.on) {
            opacity: 0;
        }
    }
</style>



// WEBPACK FOOTER //
// index.vue?51076bab